import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '@/shared/store/types';
import { logger } from '@/shared/utils/logger';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  // Subscription flow state
  checkoutSessionId: string | null;
  hasCompletedCheckout: boolean;
  pendingProviderRole: string | null; // Role from checkout, used until provider query runs
  // Actions
  setUser: (user: User | null) => void;
  setIsAuthenticated: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  setCheckoutSessionId: (sessionId: string | null) => void;
  setHasCompletedCheckout: (value: boolean) => void;
  setPendingProviderRole: (role: string | null) => void;
  clearSubscriptionFlow: () => void;
  logout: () => void;
}

// Try to get initial state from localStorage
const getInitialState = () => {
  try {
    const savedState = localStorage.getItem('auth-storage');

    if (savedState) {
      const parsedState = JSON.parse(savedState).state;
      return {
        user: parsedState.user,
        isAuthenticated: parsedState.isAuthenticated,
        isLoading: true, // Always start with loading true
      };
    }
  } catch (e) {
    logger.error('Failed to parse stored auth state:', e);
  }

  return {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    checkoutSessionId: null,
    hasCompletedCheckout: false,
    pendingProviderRole: null,
  };
};

const initialState = getInitialState();

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Use initial state or defaults
      user: initialState.user,
      isAuthenticated: initialState.isAuthenticated,
      isLoading: initialState.isLoading,
      checkoutSessionId: initialState.checkoutSessionId || null,
      hasCompletedCheckout: initialState.hasCompletedCheckout || false,
      pendingProviderRole: initialState.pendingProviderRole || null,

      // Actions
      setUser: user => {
        const currentUser = get().user;
        // Only update if value actually changes
        if (JSON.stringify(user) !== JSON.stringify(currentUser)) {
          set({ user });
        }
      },
      setIsAuthenticated: value => {
        // Only update if value actually changes
        if (value !== get().isAuthenticated) {
          set({ isAuthenticated: value });
        }
      },
      setIsLoading: value => {
        // Only update if value actually changes
        if (value !== get().isLoading) {
          set({ isLoading: value });
        }
      },
      setCheckoutSessionId: sessionId => {
        set({ checkoutSessionId: sessionId });
      },
      setHasCompletedCheckout: value => {
        set({ hasCompletedCheckout: value });
      },
      setPendingProviderRole: role => {
        set({ pendingProviderRole: role });
      },
      clearSubscriptionFlow: () => {
        set({ checkoutSessionId: null, hasCompletedCheckout: false, pendingProviderRole: null });
      },
      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          checkoutSessionId: null,
          hasCompletedCheckout: false,
          pendingProviderRole: null,
        });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        checkoutSessionId: state.checkoutSessionId,
        hasCompletedCheckout: state.hasCompletedCheckout,
        pendingProviderRole: state.pendingProviderRole,
      }),
    },
  ),
);
